<template>
  <div class="full-screen">
    <PulsBackground background-variant="3">
      <nav class="navbar bg-light mb-10">
        <div
          class="container-fluid d-grid align-items-center gap-10"
          style="grid-template-columns: 1fr auto 1fr"
        >
          <!-- LEFT COLUMN (1fr) -->
          <div class="d-flex justify-content-start">
            <button
              class="btn btn-sm btn-outline-secondary"
              :disabled="sliderIndex === 0"
              @click="prevSlide"
            >
              <i class="bi bi-arrow-left pe-0"></i>
            </button>
          </div>

          <!-- CENTER COLUMN (auto) -->
          <div class="text-center">
            {{ sliderIndex + 1 }} / {{ slideCount }}
          </div>

          <!-- RIGHT COLUMN (1fr) -->
          <div class="d-flex justify-content-end gap-10 align-items-center">
            <div class="form-check form-switch mb-0">
              <input
                id="showSensitiveCheck"
                v-model="showSensitive"
                class="form-check-input"
                type="checkbox"
              />
              <label class="form-check-label" for="showSensitiveCheck">
                {{ $t('resultDetails.exportModal.includeSensitiveQuestions') }}
              </label>
            </div>
            <button class="btn btn-sm btn-secondary" @click="didCloseSlideshow">
              <i class="bi bi-fullscreen-exit pe-0 fs-6"></i>
            </button>
            <button
              class="btn btn-sm btn-outline-secondary"
              :disabled="sliderIndex === slideCount - 1"
              @click="nextSlide"
            >
              <i class="bi bi-arrow-right pe-0"></i>
            </button>
          </div>
        </div>
      </nav>

      <!-- :a11y="false"
      :preventClicks="false"
      :preventClicksPropagation="false"
      :touchStartPreventDefault="false"
      :touchMoveStopPropagation="false" -->
      <Swiper
        class="puls-slideshow"
        :slidesPerView="1"
        :spaceBetween="50"
        :allowTouchMove="false"
        noSwipingSelector="button"
        @swiper="onSwiperInit"
      >
        <SwiperSlide v-for="widget in slideShowWidgets" :key="widget.id">
          <div class="ms-5 me-5 pe-none">
            <div class="h-100">
              <div
                v-if="
                  deregisteredWidgets[widget.id] ||
                  widget.props.loading === undefined ||
                  widget.props.loading === true
                "
                class="h-100"
              >
                <PulsSkeletonLoader
                  :skeletonInfo="
                    widgetMap[widget.type].skeleton({
                      colData: widget.data,
                      widgetProps: widget.props
                    })
                  "
                  :deregisteredWidget="deregisteredWidgets[widget.id]"
                />
              </div>
              <div v-else class="h-100">
                <component
                  :is="widgetMap[widget.type].widget"
                  v-bind="widget.props"
                  :id="widget.id"
                  class="fullscreen-slide"
                  :updateWidgetSliderState="updateWidgetSliderState"
                  :widgetSliderState="widgetSliderStates[widget.id]"
                  :updateWidgetRequests="updateWidgetRequests"
                  :deregisterWidget="deregisterWidget"
                />
              </div>
            </div>
            <!-- <NotImplementedWidget v-else :type="widget.type" /> -->
          </div>
        </SwiperSlide>
      </Swiper>
    </PulsBackground>
  </div>
</template>

<script>
import { Swiper, SwiperSlide } from 'swiper/vue'

import { getForceIncludedAndNotExcludedCategories } from '@/helpers/results-helper'
import NotImplementedWidget from '@/components/widgets/not-implemented-widget/NotImplementedWidget.vue'
import PulsSkeletonLoader from '@/components/shared/PulsSkeletonLoader.vue'

export default {
  name: 'WidgetSlideshow',
  components: {
    NotImplementedWidget,
    PulsSkeletonLoader,
    Swiper,
    SwiperSlide
  },
  props: {
    deregisteredWidgets: {
      type: Object,
      default: () => {}
    },
    widgetsForSlides: {
      type: Array,
      default: () => []
    },
    selectedGroup: {
      type: Object,
      default: () => {}
    },
    widgetRegistrations: {
      type: Object,
      default: () => {}
    },
    didCloseSlideShowCallback: {
      type: Function,
      default: () => {}
    },

    organizationName: {
      type: String,
      default: ''
    },
    propsForWidget: {
      type: Function,
      default: () => {}
    },
    widgetMap: {
      type: Object,
      default: () => {}
    },
    widgetSliderStates: {
      type: Object,
      default: () => {}
    },
    updateWidgetSliderState: {
      type: Function,
      default: () => {}
    },
    updateWidgetRequests: {
      type: Function,
      default: () => {}
    },
    deregisterWidget: {
      type: Function,
      default: () => {}
    }
  },
  data() {
    return {
      swiperInstance: null,
      showSensitive: false,
      shouldUseTrueFullScreen: true // disable for easier development
    }
  },
  computed: {
    slideShowWidgets() {
      // const addWidgets = {
      //   'categories_bars': 'category_questions'
      // }
      const sensitiveWidgets = ['alert_box', 'group_actions', 'insights']
      const defaultNumberOfQuestionsPerCategory = 8

      return this.widgetsForSlides.reduce((widgetsToKeep, item) => {
        if (
          !this.deregisteredWidgets[item.id] &&
          (this.showSensitive || !sensitiveWidgets.includes(item.type))
        ) {
          widgetsToKeep.push(item)
        }
        // Artificially add category questions widget after categories bars widget
        if (item.type === 'categories_bars') {
          const categoriesDetails =
            item.props.surveyDetails?.details?.categories || []
          const forceIncludedAndNotExcludedCategories =
            getForceIncludedAndNotExcludedCategories({
              categoriesResult: item.props.categories,
              surveyDetailsCategories: categoriesDetails,
              categoryOptions: item.data
            })
          forceIncludedAndNotExcludedCategories.forEach(
            (categoryWithDetails) => {
              const categoryValue =
                item.props.categories[categoryWithDetails.name]?.value

              let numberOfQuestionsForThisCategory =
                defaultNumberOfQuestionsPerCategory

              const questionsForThisWidget =
                categoryWithDetails?.questions.reduce((questions, q) => {
                  const questionWithAnswers = item.props.questions[q.name]
                  // always merge questions with precondition for full screen
                  const numberOfMergedQuestionsForThisQuestion =
                    categoryWithDetails.questions.filter(
                      (q2) =>
                        q2.details.precondition &&
                        q2.details.precondition === q.name
                    ).length
                  numberOfQuestionsForThisCategory +=
                    numberOfMergedQuestionsForThisQuestion
                  if (
                    !questionWithAnswers ||
                    questionWithAnswers.type === 'freetext'
                  ) {
                    return questions
                  }
                  const isSensitive = questionWithAnswers.isHarassmentQuestion
                  if (!isSensitive || this.showSensitive) {
                    questions.push({ ...questionWithAnswers, name: q.name })
                  }
                  return questions
                }, [])
              const numberOfQuestionSlides = Math.ceil(
                questionsForThisWidget.length / numberOfQuestionsForThisCategory
              )
              for (let i = 0; i < numberOfQuestionSlides; i++) {
                const questionsForThisWidgetSlide =
                  questionsForThisWidget.slice(
                    i * numberOfQuestionsForThisCategory,
                    (i + 1) * numberOfQuestionsForThisCategory
                  )
                const questionsForThisWidgetObject =
                  questionsForThisWidgetSlide.reduce((acc, q) => {
                    acc[q.name] = q
                    return acc
                  }, {})
                widgetsToKeep.push({
                  id: `${item.id}_category_${categoryWithDetails.name}_slide_${i}`,
                  type: 'category_questions',
                  props: {
                    loading: false,
                    category: {
                      ...categoryWithDetails,
                      questions: questionsForThisWidgetObject,
                      value: categoryValue
                    }
                  }
                })
              }
            }
          )
        }
        return widgetsToKeep
      }, [])
    },
    sliderIndex() {
      return this.swiperInstance?.activeIndex
    },
    slideCount() {
      return this.swiperInstance?.slides.length
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.toggleFullscreen()
    })
    window.addEventListener('keydown', this.handleKeydown)
    document.addEventListener('fullscreenchange', this.handleFullscreenChange)
  },

  beforeUnmount() {
    window.removeEventListener('keydown', this.handleKeydown)
    document.removeEventListener(
      'fullscreenchange',
      this.handleFullscreenChange
    )
  },
  methods: {
    handleClick(swiper, event) {},
    onSwiperInit(swiper) {
      this.swiperInstance = swiper
    },
    toggleFullscreen() {
      if (!this.shouldUseTrueFullScreen) {
        return
      }
      const elem = this.$el // The root element of this component

      if (!document.fullscreenElement) {
        elem.requestFullscreen().catch((err) => {
          console.error(
            `Error attempting to enable full-screen mode: ${err.message}`
          )
        })
      } else {
        document.exitFullscreen()
      }
    },

    handleFullscreenChange() {
      if (!document.fullscreenElement) {
        // If full-screen mode is exited, also close the slideshow
        this.didCloseSlideshow()
      }
    },

    didCloseSlideshow() {
      this.toggleFullscreen()
      this.didCloseSlideShowCallback()
    },

    handleKeydown(event) {
      if (!this.swiperInstance) return

      switch (event.key) {
        case 'ArrowLeft':
          this.swiperInstance.slidePrev()
          break
        case 'ArrowRight':
        case ' ':
          this.swiperInstance.slideNext()
          break
        case 'Escape':
          // Escape will first trigger fullscreen exit, which then triggers `handleFullscreenChange`
          document.exitFullscreen()
          break
      }
    },
    prevSlide() {
      if (this.swiperInstance) {
        this.swiperInstance.slidePrev()
      }
    },
    nextSlide() {
      if (this.swiperInstance) {
        this.swiperInstance.slideNext()
      }
    }
  }
}
</script>

<style lang="scss">
@use 'sass:math';

.pe-none {
  pointer-events: none;
}

.fullscreen-slide {
  max-height: 93vh;
}

.visible-fullscreen {
  visibility: hidden;
}

.full-screen {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  z-index: 9999;
  background-color: white; /* Optional: set background to suit your needs */
  .visible-fullscreen {
    visibility: visible;
  }
  .hide-fullscreen {
    visibility: hidden;
  }
  .remove-fullscreen {
    display: none !important;
  }
  h1 {
    font-size: 4rem;
  }

  .card-body {
    font-size: 1.2rem;
  }

  @for $i from 1 through 5 {
    .fs-#{$i} {
      font-size: #{3 - math.div($i, 2.6)}rem !important;
    }
  }
}
</style>
